import { Injectable } from '@angular/core';
import {CrudService} from './services';
import {Invoice} from './dto/invoice';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {DataListModel} from './models/data-list-model';
import {Payment} from './dto/payment';
import {DebtorServiceLogEntity} from './dto/debtor-service-log-entity';
import {DateTableFilter} from './table-datasouces/date-table-filter';
import {PrevNextModel} from './models/prev-next-model';
import {toISODateString, toServerDateString} from './providers/app-date-adapter';
import {AnnualFeeStatsFilteringModel} from './models/annual-fee-stats-filtering-model';
import {AnnualFeeStatsModel} from './models/annual-fee-stats-model';
import {BankTransferLoad, LoadTimeline} from "./dto/bankTransferLoad";
import {Sofort} from './dto/sofort';
import {Sepa} from './dto/sepa';
import {Upload} from './dto/upload';
import {GpsLoadResponseFileImportResult} from './dto/gps-load-response-file-import-result';
import {IdvUploadDto} from './dto/idv-upload';
import {BankTransferLoadTransaction, SelfLoadDeterminationModel} from "./dto/trustPayTransaction";
import {PrevNextIdModel} from "./models/prev-next-id-model";
import {publish} from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class BankTransferLoadService implements CrudService<BankTransferLoad> {

  readonly apiPath = 'bank-transfer-load/';

  constructor(private http: HttpClient) { }
  get(id: any) {
    return this.http.get(this.apiPath + id) as Observable<BankTransferLoad>;
  }
  getMany(
    pageIndex: number,
    pageSize: number,
    sortField: string,
    sortDirection: string,
    filterString: string,
    dateFilterCols: string[],
    startFilterDate: Date,
    endFilterDate: Date,
    someAdditionalData: string) {
    const params = new HttpParams()
      .set('page', pageIndex.toString())
      .set('pageSize', pageSize.toString())
      .set('filterString', filterString)
      .set('sortField', sortField)
      .set( 'sortDirection', sortDirection )
      .set( 'someAdditionalData', someAdditionalData );
    return this.http.get(this.apiPath + 'pending', { params }) as Observable<DataListModel<BankTransferLoad>>;
  }

  getByCardId( cardId: number ): Observable<BankTransferLoad[]> {
    return this.http.get(this.apiPath+'by-card-id/'+cardId) as Observable<BankTransferLoad[]>;
  }

  uploadGpsResponseImportCsv(file: File): Observable<GpsLoadResponseFileImportResult[]> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(this.apiPath + 'gps-response-upload', file) as Observable<GpsLoadResponseFileImportResult[]>;
  }


  add(item: BankTransferLoad): Observable<Object> {
    throw new Error('Method not implemented.');
  }
  update(item: Invoice) {
    return this.http.put(this.apiPath, item) as Observable<BankTransferLoad>;
  }

  delete(id: any) {
    return this.deleteMany([id]);
  }
  restore(id: any) {
    return this.restoreMany([id]);
  }

  deleteMany(idList: any[]): Observable<Object> {
    let httpParams = new HttpParams();

    idList.forEach(id => {
      httpParams = httpParams.append('list', id.toString());
    });

    return this.http.delete(this.apiPath, { params: httpParams });
  }

  restoreMany(idList: any[]): Observable<Object> {
    let httpParams = new HttpParams();
    idList.forEach(id => {
      httpParams = httpParams.append('list', id.toString());
    });
    return this.http.patch(this.apiPath, null, { params: httpParams });
  }


  getPrevPending(filters: DateTableFilter, bankTransferLoadId: number): Observable<number> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${bankTransferLoadId}/prev-pending`, { params }) as Observable<number>;
  }

  getNextPending(filters: DateTableFilter, bankTransferLoadId: number): Observable<number> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${bankTransferLoadId}/next-pending`, { params }) as Observable<number>;
  }

  getPrevNextPending(filters: DateTableFilter, bankTransferLoadId: number): Observable<PrevNextIdModel> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${bankTransferLoadId}/prev-next-pending`, { params }) as Observable<PrevNextIdModel>;
  }

  getPrevNextPreApproved(filters: DateTableFilter, bankTransferLoadId: number): Observable<PrevNextIdModel> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${bankTransferLoadId}/prev-next-pre-approved`, { params }) as Observable<PrevNextIdModel>;
  }

  getPrevNextDelayed(filters: DateTableFilter, bankTransferLoadId: number): Observable<PrevNextIdModel> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${bankTransferLoadId}/prev-next-delayed`, { params }) as Observable<PrevNextIdModel>;
  }

  getPrevNextLoadHistory(filters: DateTableFilter, bankTransferLoadId: number): Observable<PrevNextIdModel> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${bankTransferLoadId}/prev-next-load-history`, { params }) as Observable<PrevNextIdModel>;
  }

  getPrevNextRefund(filters: DateTableFilter, bankTransferLoadId: number): Observable<PrevNextIdModel> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${bankTransferLoadId}/prev-next-refund`, { params }) as Observable<PrevNextIdModel>;
  }

  getPrevNextRefundHistory(filters: DateTableFilter, bankTransferLoadId: number): Observable<PrevNextIdModel> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${bankTransferLoadId}/prev-next-refund-history`, { params }) as Observable<PrevNextIdModel>;
  }



  // use these below for pending loads (not confirmed)
  getPrev(filters: DateTableFilter, bankTransferLoadId: number): Observable<number> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${bankTransferLoadId}/prev`, { params }) as Observable<number>;
  }

  getNext(filters: DateTableFilter, bankTransferLoadId: number): Observable<number> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${bankTransferLoadId}/next`, { params }) as Observable<number>;
  }

  getPrevNext(filters: DateTableFilter, bankTransferLoadId: number): Observable<PrevNextModel> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${bankTransferLoadId}/prev-next`, { params }) as Observable<PrevNextModel>;
  }





  getFilterParams(filters: DateTableFilter): HttpParams {
    const str =  filters != null ? filters.str || '' : '';
    const dateFilterCols = filters != null ? filters.dateFilterCols || [] : [];
    const startFilterDate = filters != null ? filters.startFilterDate || null : null;
    const endFilterDate = filters != null ? filters.endFilterDate || null : null;
    const someAdditionalData = filters != null ? filters.someAdditionalData || null : null;

    let params = new HttpParams()


      .set('filterString', str)
      .set('startFilterDate', toISODateString(startFilterDate))
      .set('endFilterDate', toISODateString(endFilterDate))
      .set('someAdditionalData', someAdditionalData);

    if (filters != null && filters.sortField) {
      params = params.set('sortField', filters.sortField);
    }
    if (filters != null && filters.sortDirection) {
      params = params.set('sortDirection', filters.sortDirection);
    }

    if (filters != null && filters.enumFilter) {
      params = params.set('enumFilter', filters.enumFilter.value+'_'+filters.enumFilter.key);
    }


    if (dateFilterCols instanceof Array) {
      dateFilterCols.forEach(element => {
        params = params.append('dateFilterCols', element);
      });
    }

    return params;
  }

  approveLoad(id: number): Observable<BankTransferLoad> {
    return this.http.post(this.apiPath +'approve', {id}) as Observable<BankTransferLoad>;
  }

  requestSof(id: number): Observable<BankTransferLoad> {
    return this.http.post(this.apiPath +'request-sof', {id}) as Observable<BankTransferLoad>;
  }

  requestPoa(id: number): Observable<BankTransferLoad> {
    return this.http.post(this.apiPath +'request-poa', {id}) as Observable<BankTransferLoad>;
  }


  declineLoad(id: number): Observable<BankTransferLoad> {
    return this.http.post(this.apiPath +'decline', {id}) as Observable<BankTransferLoad>;
  }

  holdLoad(id: number): Observable<BankTransferLoad> {
    return this.http.post(this.apiPath +'hold', {id}) as Observable<BankTransferLoad>;
  }


  holdLoads( idList: any[] ): Observable<Object>  {
    let httpParams = new HttpParams();
    idList.forEach(id => {
      httpParams = httpParams.append( 'list', id.toString() );
    } );
    return this.http.get( this.apiPath + 'holds', { params: httpParams } );
  }

  executeLoad(id: number): Observable<BankTransferLoad> {
    return this.http.post(this.apiPath +'execute', {id}, {headers:{'skip-error-snack':'true'} }) as Observable<BankTransferLoad>;
  }

  confirmLoadManually(id: number): Observable<BankTransferLoad> {
    return this.http.post(this.apiPath +'confirm-manually', {id}) as Observable<BankTransferLoad>;
  }

  recalcLoad(id: number): Observable<BankTransferLoad> {
    return this.http.post(this.apiPath +'recalc', {id}) as Observable<BankTransferLoad>;
  }

  getLoads(cardId: number): Observable<BankTransferLoad[]> {
    return this.http.get(this.apiPath +'load-history/' + cardId) as Observable<BankTransferLoad[]>;
  }

  getCustomerLoads(customerId: number): Observable<BankTransferLoad[]> {

    const params = new HttpParams()
      .set('customerId', customerId)
    ;
    return this.http.get(this.apiPath +'load-history-customer', {params}) as Observable<BankTransferLoad[]>;
  }

  getLoadTimeline(publicToken: string): Observable<LoadTimeline> {
    const params = new HttpParams()
      .set('publicToken', publicToken)
    ;
    return this.http.get(this.apiPath + 'load-timeline', {params}) as Observable<LoadTimeline>;
  }

  getMaxDailyLoad(bankTransferLoadId: number): Observable<number> {
    return this.http.get(this.apiPath + bankTransferLoadId + '/max-daily-load') as Observable<number>;
  }

  getSelfLoadDetermination(bankTransferLoadId: number): Observable<SelfLoadDeterminationModel> {
    return this.http.get(this.apiPath + bankTransferLoadId + '/self-load-determination') as Observable<SelfLoadDeterminationModel>;
  }

  getSepas(cardId: number): Observable<Sepa[]> {
    return this.http.get(this.apiPath +'sepa-history/' + cardId) as Observable<Sepa[]>;
  }
  getSoforts(cardId: number): Observable<Sofort[]> {
    return this.http.get(this.apiPath +'sofort-history/' + cardId) as Observable<Sofort[]>;
  }

  downloadLoads(): Observable<HttpResponse<Blob>> {
      return this.http.get( this.apiPath + 'loads-file', { params: null, responseType: 'blob', observe: 'response' } );
  }

  getLastLoadFiles(): Observable<Upload[]> {
    return this.http.get(this.apiPath +'last-load-files') as Observable<Upload[]>;
  }

  getFile( fileId: number ): Observable<HttpResponse<Blob>> {
    return this.http.get( this.apiPath + 'get-file/' + fileId + '/response-file', { params: null, responseType: 'blob', observe: 'response' } );
  }

  markForRefund(id: number) {
    return this.http.post(this.apiPath +'mark-for-refund', {id}) as Observable<BankTransferLoad>;
  }

  getSofSingleUploads(btlId: number): Observable<IdvUploadDto[]> {
    return this.http.get( this.apiPath + 'get-sof-single-uploads/' + btlId) as Observable<IdvUploadDto[]>;
  }

  getOutboundTransactions(btlId: number) {
    return this.http.get(`${this.apiPath}${btlId}/outbound-transactions`, ) as Observable<BankTransferLoadTransaction[]>;
  }

  getPreApproved(pageIndex: number, pageSize: number, sortField: string, sortDirection: string, filterString: string, dateFilterCols: string[], startFilterDate: Date, endFilterDate: Date, someAdditionalData: string, additionalFilterData: string[], enumFilter: {key: string; value: string}, companiesFilter: string[]) {
    const params = new HttpParams()
      .set('page', pageIndex.toString())
      .set('pageSize', pageSize.toString())
      .set('filterString', filterString)
      .set('sortField', sortField)
      .set( 'sortDirection', sortDirection )
      .set( 'someAdditionalData', someAdditionalData );
    return this.http.get(this.apiPath + 'pre-approved', { params }) as Observable<DataListModel<BankTransferLoad>>;
  }

  getDelayed(pageIndex: number, pageSize: number, sortField: string, sortDirection: string, filterString: string, dateFilterCols: string[], startFilterDate: Date, endFilterDate: Date, someAdditionalData: string, additionalFilterData: string[], enumFilter: {key: string; value: string}, companiesFilter: string[]) {
    const params = new HttpParams()
      .set('page', pageIndex.toString())
      .set('pageSize', pageSize.toString())
      .set('filterString', filterString)
      .set('sortField', sortField)
      .set( 'sortDirection', sortDirection )
      .set( 'someAdditionalData', someAdditionalData );
    return this.http.get(this.apiPath + 'delayed', { params }) as Observable<DataListModel<BankTransferLoad>>;
  }

    executeG3Load(id: number) {
      return this.http.post(this.apiPath +'execute-g3', {id}, {headers:{'skip-error-snack':'true'} }) as Observable<BankTransferLoad>;
    }
}
